import { Logo } from "@/components/brand/Logo";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { authService } from "@/lib/api";
import { isFirebaseEnabled, signInWithGoogle } from "@/lib/firebase";
import { useAuthStore } from "@/stores/auth";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation } from "@tanstack/react-query";
import { isAxiosError } from "axios";
import { Info, Loader2 } from "lucide-react";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { z } from "zod";

type Mode = "login" | "register";

const schema = z
  .object({
    email: z.string().email("올바른 이메일을 입력하세요"),
    password: z.string().min(1, "비밀번호를 입력하세요"),
    name: z.string().optional(),
    mode: z.enum(["login", "register"]),
  })
  .refine((v) => v.mode === "login" || (v.name?.trim().length ?? 0) > 0, {
    path: ["name"],
    message: "이름을 입력하세요",
  });

type FormValues = z.infer<typeof schema>;

export function AdminLoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const login = useAuthStore((s) => s.login);
  const setUser = useAuthStore((s) => s.setUser);
  const token = useAuthStore((s) => s.token);
  const [mode, setMode] = useState<Mode>("login");
  // 403 from /auth (beta gate, disabled account) — long notice, shown inline not as toast.
  const [gateMessage, setGateMessage] = useState<string | null>(null);

  const extractGateMessage = (err: unknown): string | null =>
    isAxiosError(err) && err.response?.status === 403 && typeof err.response.data?.detail === "string"
      ? err.response.data.detail
      : null;

  // Open-redirect guard: only honor returnTo when it stays within /admin.
  const returnTo = new URLSearchParams(location.search).get("returnTo");
  const postLoginPath = returnTo?.startsWith("/admin") ? returnTo : "/admin";

  useEffect(() => {
    if (token) navigate(postLoginPath, { replace: true });
  }, [token, navigate, postLoginPath]);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { email: "", password: "", name: "", mode: "login" },
  });

  const mutation = useMutation({
    mutationFn: async (values: FormValues) => {
      const auth =
        values.mode === "login"
          ? await authService.login({ email: values.email, password: values.password })
          : await authService.register({
              email: values.email,
              password: values.password,
              name: values.name?.trim() ?? "",
            });
      login(auth.access_token);
      const me = await authService.me();
      setUser(me);
      return me;
    },
    onSuccess: () => {
      navigate(postLoginPath, { replace: true });
    },
    onError: (err: unknown) => {
      const gate = extractGateMessage(err);
      if (gate) {
        setGateMessage(gate);
        return;
      }
      toast.error(mode === "login" ? "로그인에 실패했습니다." : "가입에 실패했습니다.");
    },
  });

  const googleMutation = useMutation({
    mutationFn: async () => {
      const idToken = await signInWithGoogle();
      const auth = await authService.googleLogin(idToken);
      login(auth.access_token);
      const me = await authService.me();
      setUser(me);
      return me;
    },
    onSuccess: () => {
      navigate(postLoginPath, { replace: true });
    },
    onError: (err: unknown) => {
      // User dismissing the popup isn't an error worth surfacing.
      if (
        typeof err === "object" &&
        err !== null &&
        "code" in err &&
        (err as { code: string }).code === "auth/popup-closed-by-user"
      ) {
        return;
      }
      const gate = extractGateMessage(err);
      if (gate) {
        setGateMessage(gate);
        return;
      }
      toast.error("Google 로그인에 실패했습니다. 권한이 있는 계정인지 확인하세요.");
    },
  });

  const switchMode = (next: Mode) => {
    setMode(next);
    setGateMessage(null);
    reset({ email: "", password: "", name: "", mode: next });
  };

  return (
    <div className="bg-atmosphere grain flex min-h-dvh items-center justify-center px-4 py-10">
      <div className="relative z-10 grid w-full max-w-4xl animate-rise overflow-hidden rounded-3xl border border-border/60 bg-card shadow-float lg:grid-cols-[1.05fr_1fr]">
        {/* Brand panel — illustrated welcome scene (desktop only). */}
        <div className="relative hidden min-h-[34rem] lg:block">
          <img
            src="/brand/welcome-scene.png"
            alt=""
            className="absolute inset-0 h-full w-full object-cover object-[50%_55%]"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[hsl(0_0%_4%/0.82)] via-[hsl(0_0%_6%/0.35)] to-[hsl(0_0%_6%/0.45)]" />
          <div className="absolute inset-0 flex flex-col justify-between p-8 text-[hsl(0_0%_98%)]">
            <Logo size={40} />
            <div className="space-y-3">
              <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-[hsl(0_0%_72%)]">
                Guest Concierge Console
              </p>
              <p className="font-display text-3xl font-bold leading-[1.15] tracking-tight">
                게스트의 모든 순간을
                <br />
                품격 있게 안내합니다
              </p>
              <p className="max-w-xs text-sm text-[hsl(0_0%_88%)]/90">
                체크인 안내부터 주변 정보까지, 숙소의 디지털 컨시어지를 운영하세요.
              </p>
            </div>
          </div>
        </div>

        {/* Form panel. */}
        <div className="flex flex-col justify-center p-6 sm:p-10">
          <Card className="border-0 bg-transparent shadow-none">
            <CardHeader className="items-center gap-2 px-0 text-center lg:items-start lg:text-left">
              <Logo size={40} withWordmark className="lg:hidden" />
              <CardTitle className="mt-1 font-display text-2xl font-medium tracking-tight">
                {isFirebaseEnabled
                  ? "다시 오신 것을 환영합니다"
                  : mode === "login"
                    ? "다시 오신 것을 환영합니다"
                    : "첫 관리자 등록"}
              </CardTitle>
              <p className="text-sm text-muted-foreground">
                {isFirebaseEnabled
                  ? "Google 계정으로 로그인하세요."
                  : mode === "login"
                    ? "관리자 계정으로 로그인하세요."
                    : "콘솔을 사용할 첫 관리자를 만들어주세요."}
              </p>
            </CardHeader>
            <CardContent className="px-0">
              {gateMessage ? (
                <div
                  role="alert"
                  className="mb-4 flex gap-2 rounded-lg border border-primary/30 bg-primary/5 p-3 text-sm leading-relaxed text-foreground"
                >
                  <Info className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                  <p>{gateMessage}</p>
                </div>
              ) : null}
              {isFirebaseEnabled ? (
                <Button
                  type="button"
                  variant="outline"
                  className="w-full gap-2"
                  disabled={googleMutation.isPending}
                  onClick={() => googleMutation.mutate()}
                >
                  {googleMutation.isPending ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <GoogleIcon className="h-4 w-4" />
                  )}
                  Google로 로그인
                </Button>
              ) : (
                <>
                  <form onSubmit={handleSubmit((v) => mutation.mutate(v))} className="space-y-4">
                    <input type="hidden" {...register("mode")} />
                    {mode === "register" ? (
                      <div className="space-y-1.5">
                        <Label htmlFor="name">이름</Label>
                        <Input
                          id="name"
                          {...register("name")}
                          placeholder="홍길동"
                          autoComplete="name"
                        />
                        {errors.name ? (
                          <p className="text-xs text-destructive">{errors.name.message}</p>
                        ) : null}
                      </div>
                    ) : null}
                    <div className="space-y-1.5">
                      <Label htmlFor="email">이메일</Label>
                      <Input
                        id="email"
                        type="email"
                        {...register("email")}
                        placeholder="admin@example.com"
                        autoComplete="email"
                      />
                      {errors.email ? (
                        <p className="text-xs text-destructive">{errors.email.message}</p>
                      ) : null}
                    </div>
                    <div className="space-y-1.5">
                      <Label htmlFor="password">비밀번호</Label>
                      <Input
                        id="password"
                        type="password"
                        {...register("password")}
                        placeholder="••••••••"
                        autoComplete={mode === "login" ? "current-password" : "new-password"}
                      />
                      {errors.password ? (
                        <p className="text-xs text-destructive">{errors.password.message}</p>
                      ) : null}
                    </div>
                    <Button type="submit" className="w-full" disabled={mutation.isPending}>
                      {mutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                      {mode === "login" ? "로그인" : "관리자 등록"}
                    </Button>
                  </form>
                  <div className="mt-4 text-center text-sm text-muted-foreground">
                    {mode === "login" ? (
                      <button
                        type="button"
                        className="font-medium text-primary underline-offset-4 hover:underline"
                        onClick={() => switchMode("register")}
                      >
                        첫 관리자 등록하기
                      </button>
                    ) : (
                      <button
                        type="button"
                        className="font-medium text-primary underline-offset-4 hover:underline"
                        onClick={() => switchMode("login")}
                      >
                        로그인으로 돌아가기
                      </button>
                    )}
                  </div>
                </>
              )}
              <p className="mt-6 text-center text-xs leading-relaxed text-muted-foreground lg:text-left">
                로그인 또는 가입 시{" "}
                <Link
                  to="/terms"
                  className="font-medium text-foreground/80 underline underline-offset-2 hover:text-foreground"
                >
                  이용약관
                </Link>{" "}
                및{" "}
                <Link
                  to="/privacy"
                  className="font-medium text-foreground/80 underline underline-offset-2 hover:text-foreground"
                >
                  개인정보 처리방침
                </Link>
                에 동의하는 것으로 간주됩니다.
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}

function GoogleIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 48 48" aria-hidden="true">
      <path
        fill="#EA4335"
        d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"
      />
      <path
        fill="#4285F4"
        d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"
      />
      <path
        fill="#FBBC05"
        d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"
      />
      <path
        fill="#34A853"
        d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"
      />
    </svg>
  );
}
